import { useRouter } from "next/router";
import { Security, useOktaAuth } from "@okta/okta-react";
import { useMutation } from "@tanstack/react-query";
import React, { useState, ReactNode, useEffect } from "react";
import { OktaAuth, toRelativeUrl } from "@okta/okta-auth-js";

import {
  parseApiErrorMessage,
  createUrlFromNextRouter,
  getSHA256Hash,
} from "@/global/utils/utils";
import { routes } from "@/global/routes";
import { LoginApi } from "@/global/api/auth";
import { useAppDispatch } from "@/global/store";
import { userState } from "@/global/store/data/user";
import { useToggle } from "@/shared/base/hooks/useToggle";
import { globalConfig } from "@/global/config/global_config";
import { useTeamConfig } from "@/global/hooks/useTeamConfig";
import { createBase64String } from "@/global/utils/auth.utils";
import { authOriginUIDs, authUIDs } from "@/global/config/constants";
import { LoginErrorModal } from "@/shared/modules/AuthModules/LoginErrorModal";
import { useRouterExtended } from "@/global/hooks/useAppSystem";
import { useMlbBallparkStore } from "@/global/store/useMlbBallparkStore";

type Props = {
  children: ReactNode;
};

type OktaTransactionStorage = {
  [key: string]: {
    transaction: {
      codeVerifier: string;
      state: string;
      nonce: string;
    };
  }
};

export function useOktaHelper() {
  const router = useRouterExtended();
  const teamConfig = useTeamConfig();
  const { teamSchema, defaultRoute, oktaClientId, oktaIssuer } = teamConfig; // prettier-ignore
  const isMlbBallparkSSOEnabled = teamConfig?.authConfigs?.isMlbBallparkSSOEnabled; // prettier-ignore
  const { mlbBallparkOktaId } = useMlbBallparkStore();
  const [loginErrorMessage, setLoginErrorMessage] = useState("");
  const dispatch = useAppDispatch();
  const loginErrorModal = useToggle();
  const code = router?.query?.code as string;

  // mlb
  // const frontendUrl = process.env.NODE_ENV === "development" ? "http://localhost:3400" : globalConfig.frontendUrl; // prettier-ignore
  const frontendUrl = globalConfig.frontendUrl; // prettier-ignore
  const redirectUri = `${frontendUrl}/mlbcallback/${teamSchema}`;
  const postLogoutRedirectUri = `${frontendUrl}/${teamSchema}`;

  const loginMutation = useMutation(LoginApi());

  function redirectToLogin() {
    return router.push({
      pathname: routes.auth,
      query: {
        teamSchema,
        authType: authUIDs.login,
      },
    });
  }

  async function handlePostOktaAuth(client: OktaAuth, state: any) {
    try {
      let redirectUri;

      if (isMlbBallparkSSOEnabled && mlbBallparkOktaId) {
      } else {
        const rawTransaction = localStorage.getItem("okta-shared-transaction-storage") as string; // prettier-ignore

        if (!code || !rawTransaction) return redirectToLogin();

        let oktaTransactionStorage: OktaTransactionStorage;

        try {
          oktaTransactionStorage = JSON.parse(rawTransaction);
        } catch (error) {
          console.log(error);
          return redirectToLogin();
        }

        const { codeVerifier, state: oktaSessionState, nonce } = oktaTransactionStorage[state].transaction; // prettier-ignore
        redirectUri = `${window.location.href}&codeVerifier=${codeVerifier}&oktaSessionState=${oktaSessionState}&nonce=${nonce}`;
      }

      // calling seasonshare login with email from okta session
      const { data } = await loginMutation.mutateAsync({
        code,
        teamSchema,
        redirectUri,
        mlbBallparkOktaId,
      });

      const decodedState = JSON.parse(window.atob(state));

      const register = data?.register;

      // generic auth redirect handling
      if (!!decodedState?.callback) {
        if (register) {
          return await router.push({
            pathname: routes.auth,
            query: {
              teamSchema,
              authType: authUIDs.register,
              callback: createBase64String(decodedState.callback),
              email: data?.register?.email,
              accountId: data?.register?.accountId,
            },
          });
        }

        if (data) {
          dispatch(userState.actions.set({ session: data, teamSchema })); // session
          return await router.push({
            pathname: decodedState?.callback?.pathname,
            query: {
              teamSchema,
              payload: createBase64String(decodedState?.callback?.payload),
            },
          });
        }
      }

      if (decodedState?.authOrigin == authOriginUIDs.generalAuth) {
        if (register) {
          const { email, accountId } = register;
          router.push({
            pathname: routes.auth,
            query: {
              authType: authUIDs.register,
              teamSchema,
              email,
              accountId,
            },
          });
          return;
        } else {
          dispatch(userState.actions.set({ session: data, teamSchema }));
          router.push({
            pathname: decodedState.pathname,
            query: decodedState.query,
          });
          return;
        }
      }

      if (decodedState?.authOrigin === authOriginUIDs.purchaseSeats) {
        if (register) {
          const { email, accountId } = register;
          let query: any = {
            authType: authUIDs.register,
            teamSchema,
            email,
            accountId,
            eventCodes: decodedState?.query?.eventCodes,
          };
          if (decodedState?.query?.packageId)
            query.packageId = decodedState.query.packageId;
          router.push({
            pathname: routes.auth,
            query,
          });
          return;
        } else {
          dispatch(userState.actions.set({ session: data, teamSchema }));
          router.push({
            pathname: decodedState.pathname,
            query: decodedState.query,
          });
          return;
        }
      }

      if (decodedState?.authOrigin === authOriginUIDs.loginPage) {
        if (register) {
          const { email, accountId } = register;
          router.push({
            pathname: routes.auth,
            query: {
              authType: authUIDs.register,
              teamSchema,
              email,
              accountId,
            },
          });
          return;
        } else {
          dispatch(userState.actions.set({ session: data, teamSchema }));
          router.push({
            pathname: decodedState.pathname,
            query: decodedState.query,
          });
          return;
        }
      }
    } catch (error: any) {
      let errorMessage = parseApiErrorMessage(error);
      setLoginErrorMessage(errorMessage);
      loginErrorModal.onOpen();
    }
  }

  async function handleCloseErrorModal() {
    let logoutRedirectUrl = createUrlFromNextRouter(defaultRoute, teamConfig);
    await router.push(logoutRedirectUrl);
    loginErrorModal.onClose();
  }

  function handleGetOktaAuthClient() {
    return new OktaAuth({
      issuer: oktaIssuer,
      clientId: oktaClientId,
      redirectUri,
      postLogoutRedirectUri,
    });
  }

  return {
    loginErrorModal,
    loginErrorMessage,
    handlePostOktaAuth,
    handleCloseErrorModal,
    handleGetOktaAuthClient,
  };
}

function OktaWindowInjector() {
  const teamConfig = useTeamConfig();
  const { oktaClientId, oktaIssuer } = teamConfig;
  const isOktaEnabled = !!oktaIssuer && !!oktaClientId;
  const { handleGetOktaAuthClient } = useOktaHelper();
  const oktaCtx = useOktaAuth();

  async function injectOktaClientToWindowSecurely() {
    oktaCtx.authState?.isAuthenticated;
    const hash = "b298769ad39c19fd2827c51117be181cc77fbfaae64787bc8cc711c18ba53706"; // prettier-ignore
    // secret is "SeasonShare256"
    window.getOktaClient = async (secret: string) => {
      const inputHash = await getSHA256Hash(secret);
      if (inputHash !== hash) console.error(new Error("Invalid secret key"));
      else return { oktaClient: handleGetOktaAuthClient(), oktaCtx };
    };
  }

  React.useEffect(() => {
    // attach okta client to window securly using SHA256 encoded string for debugging purpose
    if (isOktaEnabled) injectOktaClientToWindowSecurely();
  }, []);

  return <></>;
}

export function OktaProvider({ children }: Props) {
  const router = useRouter();

  const teamConfig = useTeamConfig();
  const { oktaClientId, oktaIssuer, defaultRoute } = teamConfig;
  const isOktaEnabled = !!oktaIssuer && !!oktaClientId;
  const loginErrorModal = useToggle();
  const [loginErrorMessage, setLoginErrorMessage] = useState("");

  const { handlePostOktaAuth, handleGetOktaAuthClient } = useOktaHelper();

  async function handleOktaAuth() {
    try {
      const client = handleGetOktaAuthClient();

      // extracting  state values
      if (client.isLoginRedirect()) {
        const params = new URLSearchParams(document.location.search);
        const state = params.get("state");
        await handlePostOktaAuth(client, state);
      }
    } catch (error) {
      console.log(error);
    }
  }

  async function restoreOriginalUri(oktaAuth: OktaAuth, originalUri: string) {
    await router.replace(
      toRelativeUrl(originalUri || "/", globalConfig.frontendUrl)
    );
  }

  async function handleCloseErrorModal() {
    let logoutRedirectUrl = createUrlFromNextRouter(defaultRoute, teamConfig);
    await router.push(logoutRedirectUrl);
    loginErrorModal.onClose();
  }

  useEffect(() => {
    if (isOktaEnabled) handleOktaAuth();
  }, []); // eslint-disable-line

  if (isOktaEnabled) {
    return (
      <>
        <LoginErrorModal
          modalProps={loginErrorModal}
          errorMessage={loginErrorMessage}
          onClose={handleCloseErrorModal}
        />
        <Security
          oktaAuth={handleGetOktaAuthClient()}
          restoreOriginalUri={restoreOriginalUri}
        >
          <OktaWindowInjector />
          {children}
        </Security>
      </>
    );
  }

  return (
    <>
      <LoginErrorModal
        modalProps={loginErrorModal}
        errorMessage={loginErrorMessage}
        onClose={handleCloseErrorModal}
      />
      {children}
    </>
  );
}
